/**
 * UndoManager - Undo/redo stacks for committed sketch mutations
 * Extracted from Application.js (pure code motion; delegation pattern).
 *
 * The stacks live on this manager. Application keeps one-line delegate
 * methods (pushUndoSnapshot/discardLastUndoSnapshot/undo/redo) so callers
 * such as SketchPlane callbacks and the ToolsPanel keep working unchanged.
 *
 * LINKED MASTER STROKES: snapshots capture the live sketch state PLUS the
 * stroke store (masters + per-layer instance tables) PLUS the layer
 * selection, so undoing an edit that detached a stroke restores both the
 * geometry AND the link.
 */
export class UndoManager {
    constructor(app) {
        this.app = app;
        this.undoStack = [];
        this.redoStack = [];
    }

    /** Plain parameter snapshot of every slice group (undoable numbers) */
    _captureSliceParams() {
        return (this.app.crossSliceSelections || []).map(sel => ({
            start: sel.start, end: sel.end, spacing: sel.spacing,
            capType: sel.capType, smooth: sel.smooth,
            generateFlushTops: sel.generateFlushTops, flipSide: sel.flipSide,
            cropToCurrentShape: sel.cropToCurrentShape,
            crossbrace: sel.crossbrace,
            crossbraceWidth: sel.crossbraceWidth,
            crossbraceBottomOffset: sel.crossbraceBottomOffset,
            curvedCaps: sel.curvedCaps, capLength: sel.capLength,
            capRadius: sel.capRadius
        }));
    }

    /**
     * Restore slice-group parameters onto the live selection objects.
     * Only when the group count still matches (selections are recreated
     * when strokes/legs change - a count mismatch means the snapshot's
     * params no longer map onto the current groups).
     */
    _restoreSliceParams(params) {
        const sels = this.app.crossSliceSelections || [];
        if (!params || params.length !== sels.length) return;
        params.forEach((p, i) => Object.assign(sels[i], p));
    }

    /** Capture the full undoable state (sketch + stroke store + selection) */
    _capture() {
        return {
            sketch: this.app.sketchPlane.captureState(),
            strokes: this.app.strokeStore ? this.app.strokeStore.captureState() : null,
            selection: [...this.app.selectedBasePlaneIndices],
            activeLayer: this.app.activeSketchLayerIndex,
            chairWidth: this.app.chairWidth,
            sliceParams: this._captureSliceParams()
        };
    }

    /**
     * Restore a snapshot: stroke store and layer selection first, then the
     * live sketch. sketchPlane.restoreState fires onShapeChange, whose SYNC
     * stage diffs the restored live sketch against the restored store -
     * a no-op since they were captured together - and re-resolves every
     * layer's state (re-linking undone detaches).
     */
    _restore(snapshot) {
        if (snapshot.strokes && this.app.strokeStore) {
            this.app.strokeStore.restoreState(snapshot.strokes);
        }
        if (snapshot.selection && this.app.basePlanes?.length) {
            this.app.selectedBasePlaneIndices = new Set(
                snapshot.selection.filter(i => i >= 0 && i < this.app.basePlanes.length));
            this.app.basePlanes.forEach((p, i) =>
                this.app.selectedBasePlaneIndices.has(i) ? p.select() : p.unselect());
        }
        if (snapshot.activeLayer !== undefined) {
            this.app.activeSketchLayerIndex = snapshot.activeLayer;
        }
        // Chair width (depth) is part of a Scale-tool edit; restore it before
        // the sketch restore so the regeneration onShapeChange fires rebuilds
        // planes at the right spacing.
        if (snapshot.chairWidth !== undefined &&
            snapshot.chairWidth !== this.app.chairWidth) {
            this.app.chairWidth = snapshot.chairWidth;
            this.app.basePlaneSpacing =
                this.app.chairWidth / Math.max(1, this.app.numBasePlanes - 1);
            this.app.updateBasePlanePositions?.();
            this.app.uiManager?.panels?.layers?.update?.();
        }
        this._restoreSliceParams(snapshot.sliceParams);
        this.app.sketchPlane.restoreState(snapshot.sketch);
        this.app.updateStatusDisplay?.();
        this.app.updateLayerMeshOpacity?.();
    }

    /**
     * Snapshot the current committed sketch state for undo. Called just
     * before every committed mutation (via SketchPlane.onBeforeCommit).
     */
    pushUndoSnapshot() {
        this.undoStack = this.undoStack || [];
        this.redoStack = [];
        this.undoStack.push(this._capture());
        if (this.undoStack.length > 20) this.undoStack.shift();
    }

    /** Discard the most recent snapshot (grab attempts that grabbed nothing) */
    discardLastUndoSnapshot() {
        if (this.undoStack && this.undoStack.length > 0) {
            this.undoStack.pop();
        }
    }

    /** Undo the last committed sketch mutation (Ctrl+Z / toolbar) */
    undo() {
        if (!this.undoStack || this.undoStack.length === 0) {
            console.log('Nothing to undo');
            return;
        }
        this.redoStack = this.redoStack || [];
        this.redoStack.push(this._capture());
        this._restore(this.undoStack.pop());
        console.log('Undo (' + this.undoStack.length + ' left)');
    }

    /** Redo the last undone mutation (Ctrl+Shift+Z / Ctrl+Y) */
    redo() {
        if (!this.redoStack || this.redoStack.length === 0) {
            console.log('Nothing to redo');
            return;
        }
        this.undoStack = this.undoStack || [];
        this.undoStack.push(this._capture());
        this._restore(this.redoStack.pop());
        console.log('Redo');
    }

    /**
     * Reset the stacks. Used after restoring an autosave: the restore
     * itself should not be undoable back to nothing.
     */
    clear() {
        this.undoStack = [];
        this.redoStack = [];
    }
}
